"use client";

import { forwardRef, useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import {
  DEFAULT_SIDE_OFFSET,
  DEFAULT_SIDE_SCALE,
} from "./RotationalCarousel.const";
import type { RotationalCarouselProps } from "./RotationalCarousel.types";
import { RotationalCarouselContext } from "./RotationalCarouselContext";

/**
 * Root carousel component. Owns all shared state and exposes it to every
 * subcomponent via `RotationalCarouselContext`.
 *
 * The carousel renders three visible slots at a time — previous, center, and
 * next — with ±2 items preloaded off-screen so transitions animate smoothly.
 * Navigation is infinite: `activeIndex` increments/decrements without bound
 * and each item maps its own `index` to a circular slot offset.
 *
 * **Composition pattern:**
 * ```tsx
 * <RotationalCarousel>
 *   <RotationalCarouselContent>
 *     <RotationalCarouselItem index={0}>...</RotationalCarouselItem>
 *     <RotationalCarouselItem index={1}>...</RotationalCarouselItem>
 *     <RotationalCarouselItem index={2}>...</RotationalCarouselItem>
 *   </RotationalCarouselContent>
 *   <RotationalCarouselPrevious />
 *   <RotationalCarouselNext />
 * </RotationalCarousel>
 * ```
 */
const RotationalCarousel = forwardRef<HTMLDivElement, RotationalCarouselProps>(
  (
    {
      children,
      className,
      sideScale = DEFAULT_SIDE_SCALE,
      sideOffset = DEFAULT_SIDE_OFFSET,
    },
    ref,
  ) => {
    const [totalItems, setTotalItems] = useState(0);

    // Intentionally unbounded.
    // circularOffset() in utils maps any integer to the correct slot,
    // so the carousel loops seamlessly without a modulo reset at the boundary.
    const [activeIndex, setActiveIndex] = useState(0);

    // Both dimensions are measured from the center card's DOM node by
    // RotationalCarouselItem and propagated here via setCardDimensions.
    // They start at 0 and are populated before the first paint via useLayoutEffect.
    const [cardWidth, setCardWidth] = useState(0);
    const [cardHeight, setCardHeight] = useState(0);

    // halfWidth is derived rather than stored so it stays in sync with cardWidth
    // without an extra state update. Passed through context so items can compute
    // translateX: visual_center = left(50%) + translateX  =>  translateX = target - halfWidth
    const halfWidth = cardWidth / 2;

    const setCardDimensions = useCallback((width: number, height: number) => {
      setCardWidth(width);
      setCardHeight(height);
    }, []);

    const goPrev = useCallback(() => setActiveIndex((i) => i - 1), []);
    const goNext = useCallback(() => setActiveIndex((i) => i + 1), []);

    return (
      <RotationalCarouselContext.Provider
        value={{
          activeIndex,
          totalItems,
          halfWidth,
          cardHeight,
          sideScale,
          sideOffset,
          setCardDimensions,
          setTotalItems,
          goPrev,
          goNext,
        }}
      >
        <div
          ref={ref}
          role="region"
          aria-roledescription="carousel"
          className={cn("tw-w-full", className)}
        >
          {children}
        </div>
      </RotationalCarouselContext.Provider>
    );
  },
);

RotationalCarousel.displayName = "RotationalCarousel";

export { RotationalCarousel };
export { RotationalCarouselContent } from "./RotationalCarouselContent";
export { RotationalCarouselItem } from "./RotationalCarouselItem";
export {
  RotationalCarouselNext,
  RotationalCarouselPrevious,
} from "./RotationalCarouselNavigation";
